// Full app router mirroring ops-portal/src/app/app-routing.module.ts.
// `handle` on each route carries title/subtitle/tab translation keys and required permissions —
// equivalent to Angular's route `data: { title, subtitle, tab, permissions }`.
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { AuthLayout } from '@/auth/AuthProvider';
import { RequireAuth } from '@/auth/RequireAuth';
import { HomePage } from '@/features/home/HomePage';
import { UserListPage } from '@/features/user-list/UserListPage';
import { UserOperationsPage } from '@/features/user-operations/UserOperationsPage';
import { NostroOperationsPage } from '@/features/nostro-operations/NostroOperationsPage';
import { AccountsPage } from '@/features/accounts/AccountsPage';
import { GlobalPricingPage } from '@/features/pricing-global/GlobalPricingPage';
import { GlobalPricingUpdatePage } from '@/features/pricing-global/GlobalPricingUpdatePage';
import { TransfersMonitoringPage } from '@/features/monitoring/TransfersMonitoringPage';
import { PaymentMonitoringPage } from '@/features/monitoring/PaymentMonitoringPage';
import { CorrectionsMonitoringPage } from '@/features/monitoring/CorrectionsMonitoringPage';
import { ManageBusinessesPage } from '@/features/manage-businesses/ManageBusinessesPage';
import { SingleBusinessPage } from '@/features/manage-businesses/SingleBusinessPage';
import { ManualIdVerificationPage } from '@/features/manual-id-verification/ManualIdVerificationPage';
import { UserInfoPage } from '@/features/manual-id-verification/UserInfoDialog';
import { BusinessEntityPricingPage } from '@/features/pricing-business-entity/BusinessEntityPricingPage';
import { BusinessEntityOverviewPage } from '@/features/pricing-business-entity/BusinessEntityOverviewPage';
import { BusinessEntityPricingUpdatePage } from '@/features/pricing-business-entity/BusinessEntityPricingUpdatePage';
import { PaymentIntermediariesPage } from '@/features/payment-intermediaries/PaymentIntermediariesPage';

function Guarded(_props: {
  permissions?: string[];
  titleKey: string;
  label: string;
}) {
  return null;
}
// Retained for future placeholder routes; intentionally unused.
void Guarded;

export const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    children: [
      {
        element: <AppLayout />,
        children: [
      {
        path: '/',
        element: (
          <RequireAuth>
            <HomePage />
          </RequireAuth>
        ),
        handle: { title: 'menus.home' },
      },
      {
        path: '/user-operations',
        element: (
          <RequireAuth>
            <UserOperationsPage />
          </RequireAuth>
        ),
        handle: { title: 'menus.userOperationNavigation' },
      },
      {
        path: '/nostro-operations',
        element: (
          <RequireAuth>
            <NostroOperationsPage />
          </RequireAuth>
        ),
        handle: { title: 'menus.nostroOperationNavigation' },
      },
      {
        path: '/accounts',
        element: (
          <RequireAuth>
            <AccountsPage />
          </RequireAuth>
        ),
        handle: { title: 'menus.menuAccounts' },
      },

      // Pricing — requires ADMIN or TREASURY
      {
        path: '/pricing',
        handle: { title: 'menus.menuPricing', permissions: ['ADMIN', 'TREASURY'] },
        children: [
          {
            path: 'global',
            handle: { title: 'menus.menuPricing', subtitle: 'menus.globalPricing' },
            children: [
              {
                index: true,
                element: (
                  <RequireAuth permissions={['ADMIN', 'TREASURY']}>
                    <GlobalPricingPage />
                  </RequireAuth>
                ),
              },
              {
                path: ':action/:operationGroup/:operation/:id',
                element: (
                  <RequireAuth permissions={['ADMIN', 'TREASURY']}>
                    <GlobalPricingUpdatePage />
                  </RequireAuth>
                ),
                handle: {
                  title: 'menus.menuPricing',
                  subtitle: 'menus.globalPricing',
                  tab: 'menus.pageTitles.pricing-details',
                },
              },
              { path: '*', element: <Navigate to="" replace /> },
            ],
          },
          {
            path: 'be',
            handle: { title: 'menus.menuPricing', subtitle: 'menus.businessEntitiesPricing' },
            children: [
              {
                index: true,
                element: (
                  <RequireAuth permissions={['ADMIN', 'TREASURY']}>
                    <BusinessEntityPricingPage />
                  </RequireAuth>
                ),
              },
              {
                path: 'details/:id',
                handle: {
                  title: 'menus.menuPricing',
                  subtitle: 'menus.businessEntitiesPricingOverview',
                },
                children: [
                  {
                    index: true,
                    element: (
                      <RequireAuth permissions={['ADMIN', 'TREASURY']}>
                        <BusinessEntityOverviewPage />
                      </RequireAuth>
                    ),
                  },
                  {
                    path: ':tab',
                    element: (
                      <RequireAuth permissions={['ADMIN', 'TREASURY']}>
                        <BusinessEntityOverviewPage />
                      </RequireAuth>
                    ),
                    handle: {
                      title: 'menus.menuPricing',
                      subtitle: 'menus.businessEntitiesPricingOverview',
                      tabTranslatePath: 'pages.pricing.be_pricing.operations',
                    },
                  },
                  {
                    path: ':action/:operationGroup/:operation',
                    element: (
                      <RequireAuth permissions={['ADMIN', 'TREASURY']}>
                        <BusinessEntityPricingUpdatePage />
                      </RequireAuth>
                    ),
                    handle: {
                      title: 'menus.menuPricing',
                      subtitle: 'menus.businessEntitiesPricingOverview',
                      tab: 'menus.pageTitles.viewEditBEDetails',
                    },
                  },
                ],
              },
            ],
          },
        ],
      },

      // Monitoring
      {
        path: '/monitoring',
        handle: { title: 'menus.monitoring' },
        children: [
          {
            path: 'transfers',
            element: (
              <RequireAuth>
                <TransfersMonitoringPage />
              </RequireAuth>
            ),
            handle: { title: 'menus.monitoring_transfers' },
          },
          {
            path: 'payments',
            element: (
              <RequireAuth>
                <PaymentMonitoringPage />
              </RequireAuth>
            ),
            handle: { title: 'menus.monitoring_payments' },
          },
          {
            path: 'corrections',
            element: (
              <RequireAuth>
                <CorrectionsMonitoringPage />
              </RequireAuth>
            ),
            handle: { title: 'menus.monitoring_corrections' },
          },
          { path: '*', element: <Navigate to="transfers" replace /> },
        ],
      },

      // Manage businesses — ADMIN only
      {
        path: '/manage-businesses',
        handle: { title: 'menus.beManagement', permissions: ['ADMIN'] },
        children: [
          {
            index: true,
            element: (
              <RequireAuth permissions={['ADMIN']}>
                <ManageBusinessesPage />
              </RequireAuth>
            ),
          },
          {
            path: 'be/:id',
            element: (
              <RequireAuth permissions={['ADMIN']}>
                <SingleBusinessPage />
              </RequireAuth>
            ),
            handle: {
              title: 'menus.beManagement',
              subtitle: 'menus.pageTitles.business_details',
            },
          },
          { path: '*', element: <Navigate to="" replace /> },
        ],
      },

      {
        path: '/manual-id-verification',
        element: (
          <RequireAuth>
            <ManualIdVerificationPage />
          </RequireAuth>
        ),
        handle: { title: 'menus.menuManualIdVerification' },
      },
      {
        path: '/manual-id-verification/user-info',
        element: (
          <RequireAuth>
            <UserInfoPage />
          </RequireAuth>
        ),
        handle: { title: 'menus.menuManualIdVerificationDetails' },
      },
      {
        path: '/user-list',
        element: (
          <RequireAuth>
            <UserListPage />
          </RequireAuth>
        ),
        handle: { title: 'menus.menuUserList' },
      },
      {
        path: '/payment-intermediaries',
        element: (
          <RequireAuth permissions={['screen:ops_operations']}>
            <PaymentIntermediariesPage />
          </RequireAuth>
        ),
        handle: { title: 'menus.menuPaymentIntermediaries', permissions: ['screen:ops_operations'] },
      },

      { path: '*', element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);
